import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AcademicCapIcon,
  BriefcaseIcon,
  CalendarIcon,
  ChartBarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

import ShortcutCard from '@/components/student-dashboard/ShortcutCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Carousel } from '@/components/ui/daisy';
import { StudentShortcut } from '@/data/hook/student-dashboard/useStudentShortcuts';
import {
  isStudentShortcutVisible,
  StudentShortcutType,
} from '@/utils/student-dashboard/getStudentShortcuts';

const ICONS: Record<StudentShortcutType, ReactNode> = {
  [StudentShortcutType.LESSONS]: (
    <AcademicCapIcon className="size-6 text-secondary" />
  ),
  [StudentShortcutType.SCHEDULE]: (
    <CalendarIcon className="size-6 text-accent" />
  ),
  [StudentShortcutType.EXAMS]: (
    <DocumentTextIcon className="size-6 text-success" />
  ),
  [StudentShortcutType.PROJECTS]: (
    <BriefcaseIcon className="size-6 text-warning" />
  ),
  [StudentShortcutType.PERFORMANCE]: (
    <ChartBarIcon className="size-6 text-info" />
  ),
};

const LABEL_KEYS: Record<StudentShortcutType, string> = {
  [StudentShortcutType.LESSONS]: 'lessons',
  [StudentShortcutType.SCHEDULE]: 'schedule',
  [StudentShortcutType.EXAMS]: 'exams',
  [StudentShortcutType.PROJECTS]: 'projects',
  [StudentShortcutType.PERFORMANCE]: 'performance',
};

const CARD_CLASS_NAMES: Record<StudentShortcutType, string> = {
  [StudentShortcutType.LESSONS]: 'bg-secondary/10',
  [StudentShortcutType.SCHEDULE]: 'bg-accent/10',
  [StudentShortcutType.EXAMS]: 'bg-success/10',
  [StudentShortcutType.PROJECTS]: 'bg-warning/10',
  [StudentShortcutType.PERFORMANCE]: 'bg-info/10',
};

type ShortcutListProps = {
  shortcuts: StudentShortcut[];
  className?: string;
};

export default function ShortcutList({
  shortcuts,
  className,
}: ShortcutListProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'studentDashboard.shortcuts',
  });

  return (
    <nav aria-label={t('title')} className={className}>
      <Carousel className="flex w-full gap-4">
        {shortcuts.map(({ type, href, count }) => (
          <ConditionalRenderer
            key={type}
            condition={isStudentShortcutVisible(type)}
          >
            <Carousel.Item className="w-30 md:w-auto md:flex-1">
              <ShortcutCard
                icon={ICONS[type]}
                label={t(LABEL_KEYS[type])}
                href={href}
                count={count}
                className={CARD_CLASS_NAMES[type]}
              />
            </Carousel.Item>
          </ConditionalRenderer>
        ))}
      </Carousel>
    </nav>
  );
}
